import { useMutation } from "react-query";

import type { UserCourseLesson } from "api/schemas/UserCourse";
import { useApiClient } from "api/useApiClient";
import { reportError } from "utils/errorReporting";

import type {
  LessonFormData,
  PostLessonPartPayload,
  PostLessonPartTranslationPayload,
  PostLessonPayload,
} from "../schemas/lessons";

export const usePostLesson = () => {
  const client = useApiClient();

  return useMutation<{ tempLesson: UserCourseLesson }, Error, LessonFormData>(async args => {
    try {
      const lessonPayload: PostLessonPayload = {
        target_joints: args.target_joints,
      };
      const lessonResponse = await client.postLesson(lessonPayload);
      const lessonID = lessonResponse?.id;

      if (!lessonID) {
        throw new Error("Failed to create lesson");
      }

      const { parts } = args;
      const part = parts[0];

      const lessonPartPayload: PostLessonPartPayload = {
        order: part.order,
        questions: part.translation.questions.map((question, index) => ({
          order: index,
          correct_alternative: question.answers.findIndex(answer => answer.correct),
        })),
      };
      const lessonPartResponse = await client.postLessonPart({ lessonID, data: lessonPartPayload });
      const lessonPartID = lessonPartResponse?.id;

      if (!lessonPartID) {
        throw new Error("Failed to create lesson part");
      }

      const lessonPartTranslationPayload: PostLessonPartTranslationPayload = {
        locale: part.translation.locale,
        title: part.translation.title,
        content: part.translation.content,
        questions: part.translation.questions,
        image_url: part.translation.image_url || null,
      };
      const lessonPartTranslationResponse = await client.postLessonPartTranslation({
        lessonID,
        lessonPartID,
        data: lessonPartTranslationPayload,
      });

      const tempLesson: UserCourseLesson = {
        ...lessonResponse,
        image_url: lessonPartTranslationResponse.image_url,
        parts_count: 1,
        title: lessonPartTranslationResponse.title,
      };
      return { tempLesson };
    } catch (error) {
      reportError("Failed to create lesson", new Error(JSON.stringify({ error, variables: args })));
      throw error;
    }
  });
};
